import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { find, map } from 'lodash-es';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { IncentivesV1ApiService } from 'apps/account/src/account-shared/services/incentives-v1-api.service';
import { ImageV1ApiService } from 'apps/account/src/account-shared/services/image-v1-api.service';
import { FtTasksService } from 'apps/account/src/account-shared/services/ft-tasks.service';
import { changesTransformer, transformType } from 'apps/account/src/account-shared/services/changes-transformer.util';
import { serializeGoalsModel } from 'apps/account/src/account-shared/services/campaign-goals.util';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditorsModule } from '../../../../account-shared/forms/editors/editors.module';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { contestDisplayHtml } from './contest-html.util';
import {
    CapitalizePipe,
    DateFormatPipe,
    DateRangePipe,
    OnOffPipe,
} from './contest-sheets.pipes';
import { FtLanguageSelectorComponent } from './ft-language-selector.component';
import { FtFinalistIncentivesComponent } from './ft-finalist-incentives.component';
import { FtGoalsPanelComponent } from './ft-goals-panel.component';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from './field-editor-modal.component';
import { computeDiffs, revertDiffs, snapshotClone } from './native-diff.util';
import { FtImageLibraryComponent } from '../touchpoint-sheets/ft-image-library.component';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtFinalistGoalSelectorComponent, FtRunnerupsComponent } from './ft-finalist-goal-selector.component';
import { FtEditGoalsComponent } from './ft-edit-goals.component';
import { FtEmailImagesPreviewComponent } from './ft-email-images-preview.component';
import { FtDateRangePickerComponent } from './ft-date-range-picker.component';
import dayjs from 'dayjs/esm/index';

const AVAILABLE_TAGS = { 'available-tags': 'b,p,a,span,div,br,strong' };

/**
 * Contest DETAILS sheet: the read-view + every edit surface (field editors via
 * FtFieldEditorModalComponent, and the date-range / image-library / theme /
 * rank-system / finalists-goals / contest-goals modals as native ft-modal-overlay
 * + ft-modal-chrome-ng). Data loads and `save()` (the full union PUT) go through
 * PhotoContestApiService.
 */
@Component({
    selector: 'ft-contest-details-ng',
    imports: [
        FormsModule,
        EditorsModule,
        CurationBackgroundImageDirective,
        TranslateModule,
        OnOffPipe,
        DateRangePipe,
        DateFormatPipe,
        CapitalizePipe,
        FtLanguageSelectorComponent,
        FtFinalistIncentivesComponent,
        FtGoalsPanelComponent,
        FtFieldEditorModalComponent,
        FtImageLibraryComponent,
        FtModalComponent,
        FtModalChromeComponent,
        FtFinalistGoalSelectorComponent,
        FtRunnerupsComponent,
        FtEditGoalsComponent,
        FtEmailImagesPreviewComponent,
        FtDateRangePickerComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './contest-details.component.html',
    styleUrls: ['./contest-details.component.scss'],
})
export class FtContestDetailsComponent implements OnInit {
    readonly contestUuid = input.required<string>();

    /** Native field-editor modal request (bool fields). `null` = closed. */
    protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);

    /** ftModal phase 2 / WS3: the native "Banner image" modal open flag + its ft-snapshot (for X/Cancel revert). */
    protected readonly bannerModalOpen = signal(false);
    private bannerSnapshot: { backgroundImage: any; isUseDefaultImage: any } | null = null;
    protected readonly bannerSwitchUid = 'ft-banner-default-image-switch';

    /** The two email-banner previews galleries (v1 `modals.emailImages.{type}.preview`), owned by
     *  FtEmailImagesPreviewComponent and opened from the panels the v1 ft-targets-modal sat on. */
    protected readonly prearrivalPreviewOpen = signal(false);
    protected readonly poststayPreviewOpen = signal(false);

    /** ftModal phase 2 / WS3: the native "Theme" modal open flag (direct-save theme grid). */
    protected readonly themeModalOpen = signal(false);
    /** ftModal phase 2 / WS3: the native "Date range" modal open flag + open-time dayjs-clone snapshot. */
    protected readonly dateRangeModalOpen = signal(false);
    private dateSnapshot: { start: any; end: any } | null = null;
    /** ftModal phase 2 / WS3: the native "Finalist ranking display" modal — open flag, dropdown open flag, snapshot. */
    protected readonly rankModalOpen = signal(false);
    protected readonly rankOpen = signal(false);
    private rankSnapshot: any = undefined;


    protected readonly data = signal<any>({});
    protected readonly model = signal<any>({
        language: null,
        propertyId: undefined,
        defaultBackgroundImageUrl: undefined,
        isUseDefaultImage: false,
        submitConsentDefaultText: '',
        phoneAcceptanceOptions: [
            { value: '2', label: 'Accept with checkbox' },
            { value: '1', label: 'Accept on submit' },
        ],
        phoneDisplayOptions: [
            { value: '2', label: 'Required' },
            { value: '1', label: 'Optional' },
            { value: '0', label: 'Off' },
        ],
    });
    protected readonly filteredIncentivesByTag = signal<any[]>([]);
    /** ftModal phase 2 / WS3: the native "Goals for finalist" modal open flag. */
    protected readonly finalistsModalOpen = signal(false);
    /** ftModal phase 2 / WS3: the native "Goals for friends" (contestGoals) modal open flag. */
    protected readonly goalsModalOpen = signal(false);
    /** Modal-open snapshot of data.contest.finalistsIncentives for the X/Cancel revert. */
    private finalistsSnapshot: any = null;
    /** Warm-lead-tagged property incentives ({item:[]}) bound by the bridged friends-goals modal. */
    protected readonly goalsIncentivesByTag = signal<any>({ item: [] });
    /** Built lazily by openGoalsModal() (v1 ftGoalsPanel.showModal) — bound by the bridged modal. */
    protected readonly editGoalsModel = signal<any>(undefined);
    isFliptoStaff = false;
    protected noneSetText = '';
    protected clickToEditText = '';

    /** ftModal phase 2: open-time deep clone of languages.item for the native contestName diff-save. */
    private languagesSnapshot: any[] | null = null;

    // WS5 #10 inc 2u: FULLY drained — Incentives/Image are the native V1Api ports,
    // ftTasks the native shared registry (downgraded back into v1 as 'ftTasks'),
    // changesTransformer/transformType/updateGoalsSerializer the native utils,
    // `_`/dayjs the same module instances, $filter('translate') -> this.translate.
    private readonly photoContest: any = inject(PhotoContestApiService);
    private readonly incentives = inject(IncentivesV1ApiService);
    private readonly imageApi = inject(ImageV1ApiService);
    private readonly spaces: any = inject(SpacesService);
    private readonly session: any = inject(SessionService);
    private readonly identity: any = inject(IdentityService);
    private readonly ftTasks = inject(FtTasksService);
    private readonly changesTransformer = changesTransformer;
    private readonly transformType = transformType;
    private readonly updateGoalsSerializer = { serialize: serializeGoalsModel };
    private readonly incentiveTags = { guest: 1, warmLead: 2, contestPrize: 4 }; // v1 .constant('incentiveTags')
    private readonly parseSvc: any = inject(ParseService);
    private readonly translate: (key: string) => string;
    private readonly eventBus = inject(EventBusService);
    private readonly zone = inject(NgZone);

    constructor() {
        const translateSvc = inject(TranslateService);
        this.translate = (key: string, params?: any) => translateSvc.instant(key, params);
        this.noneSetText = this.translate('flipto.account.NoneSet');
        this.clickToEditText = this.translate('flipto.account.ClickToEdit');
    }

    ngOnInit(): void {
        this.isFliptoStaff = this.identity.isFliptoStaff();
        const property = this.session.getProperty(this.spaces.getPropertyUuid());
        this.model().propertyId = this.spaces.getPropertyUuid();
        this.model().defaultBackgroundImageUrl = property.BackgroundImageUrl;
        // Preserve the v1 `||`-precedence quirk EXACTLY (preserve-bug for pixel/text parity).
        this.model().submitConsentDefaultText =
            'By clicking submit, you accept the rules of the contest and permit use of your story to introduce travelers to '
                + property.DisplayName || property.Name
                + ' as defined in the terms & conditions and photo release.';
        this.loadContest();
    }

    protected readonly themeLocked = computed(() => {
        // Guard undefined (pre-load): the parse SERVICE `.int()` THROWS "undefined is
        // not a number" on NaN, unlike the v1 `| parse:'number'` filter which is
        // graceful — this getter runs every CD cycle, incl. before loadContest.
        const total = this.data()?.contest?.totalSubmissions;
        return total != null && this.parseSvc.int(total) > 0;
    });

    protected get phoneConsentEnabled(): boolean {
        return this.parseIsPhoneConsentFeatureEnabled(this.data()?.contest?.isPhoneConsentFeatureEnabled);
    }
    protected set phoneConsentEnabled(v: boolean) {
        if (this.data()?.contest) {
            this.data().contest.isPhoneConsentFeatureEnabled = v;
        }
    }

    /** The read-side sink — see {@link contestDisplayHtml} for why it stays an unsanitised string. */
    protected readonly displayHtml = contestDisplayHtml;

    /**
     * The story-submission consent copy: the platform default followed by the property's own text.
     * A plain string, like {@link contestDisplayHtml} — Angular sanitises it at the `[innerHTML]`
     * binding, which is the whole point (FTK-7642).
     */
    protected submitConsentHtml(): string {
        return (this.model().submitConsentDefaultText || '') + (this.model().language?.submitConsent || '');
    }

    protected phoneAcceptanceText(modeId: any): string {
        return this.model().phoneAcceptanceOptions?.find((o: any) => o.value === modeId)?.label;
    }

    protected phoneDisplayText(modeId: any): string {
        return this.model().phoneDisplayOptions?.find((o: any) => o.value === modeId)?.label;
    }

    parseIsPhoneConsentFeatureEnabled(value: any): boolean {
        return typeof value === 'boolean' ? value : Boolean(parseInt(value, 10));
    }

    protected isV2NotAdmin(): boolean {
        return !this.identity.isFliptoStaff();
    }

    // --- fields-editor panel bridges ---

    // ftModal phase 2: bool field editors open the NATIVE modal shell
    // (FtFieldEditorModalComponent) instead of the bridged v1 ftModal — the CDK overlay
    // stacks above the CDK contest sheet (see field-editor-modal.component.ts). datasource is
    // the live `data.contest`; Done → save() (the FULL union PUT), Cancel → snapshot rollback.

    protected editAutoInvite(): void {
        this.fieldEditorConfig.set({
            type: 'bool',
            datasource: this.data().contest,
            fields: [{ name: 'isAutoInvite', label: this.translate('flipto.account.sections.platform.photoAdvocacy.AutoInviteGuests') }],
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.AutoInviteGuests'),
            description: this.translate('flipto.account.sections.platform.photoAdvocacy.AutoInviteGuestsDescription'),
            onDone: () => this.save(),
        });
    }

    protected editPrivate(): void {
        if (this.isV2NotAdmin()) return;
        this.fieldEditorConfig.set({
            type: 'bool',
            datasource: this.data().contest,
            fields: [{ name: 'isPrivate', label: this.translate('flipto.account.sections.platform.photoAdvocacy.PrivateContest') }],
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.PrivateContest'),
            description: this.translate('flipto.account.sections.platform.photoAdvocacy.PrivateContestDescription'),
            onDone: () => this.save(),
        });
    }

    protected editContestName(): void {
        // ftModal phase 2: NATIVE shell + native deep-diff save (no bridged ftModal → no CDK-overlay
        // occlusion of the editor by the contest sheet). Snapshot languages.item at open; the native
        // ft-text-field-ng mutates it in place; Done diffs live-vs-snapshot (paths [<i>,'contestName'])
        // → the SAME changesTransformer(MultiLanguageData) + PUT the v1 saveName used.
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'text',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'contestName' }],
            fieldsOptions: { 'ng-maxlength': 50 },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.ContestName'),
            onDone: () => this.saveContestName(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    /**
     * Native deep-diff replacement for the v1 bridged `saveName(diffs)`: diff the live
     * `languages.item` against the open-time snapshot (paths `[<i>,'contestName']` — the same net
     * shape v1 produced after its `splice(0,4)`), then the SAME `changesTransformer(MultiLanguageData)`
     * + PUT as the v1 DetailsController.saveName.
     */
    private saveContestName(): void {
        const diffs = computeDiffs(this.languagesSnapshot, this.data().contest.languages.item);
        const languages = this.changesTransformer(this.transformType.MultiLanguageData, diffs, this.data().contest.languages.item);
        const model = { languages: map(languages, (language: any) => ({ name: language.contestName, languageCode: language.languageCode })) };
        this.ftTasks.register('update-email-images');
        this.update(model)
            .then((args: any) => this.broadcastChanged(args))
            .finally(() => this.ftTasks.finish('update-email-images'));
    }

    /** Cancel: revert the native field's in-place edits (v1 tracker.rollback → DeepDiff.revertChange). */
    private rollbackLanguages(): void {
        if (!this.languagesSnapshot) return;
        const diffs = computeDiffs(this.languagesSnapshot, this.data().contest.languages.item);
        revertDiffs(this.data().contest.languages.item, this.languagesSnapshot, diffs);
    }

    // ftModal phase 2 #6: the FINALIST "Share message" editor — text+tokens, so the FieldEditor factory
    // upgrades it to ft-html-field mode=text (textAngular). Renders via the native FtHtmlFieldComponent
    // (NOT a plain input). SAVE is the full union PUT (save()); only CANCEL needs the snapshot rollback
    // (revert the nested language[i].guestMessage edit, like v1 ft-snapshot).
    protected editGuestMessage(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'guestMessage' }],
            fieldsOptions: { mode: 'text', tokens: ['first', 'last'] },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.FinalistMessage'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    // ftModal phase 2 #6 (sibling): the FRIEND "Share message" editor — same text+tokens → html mode=text.
    protected editFollowerMessage(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'followerMessage' }],
            fieldsOptions: { mode: 'text', tokens: ['first', 'last'] },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.FriendMessage'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    // ftModal phase 2 #7: the FIRST native WYSIWYG (html mode) field — Terms & Conditions. Rich
    // textAngular toolbar (bold/italics/underline/insertLink/ul/ol/html) via the native
    // FtHtmlFieldComponent. SAVE is the full union PUT (save()); cancel reverts the snapshot.
    protected editTerms(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'terms' }],
            fieldsOptions: { ...AVAILABLE_TAGS },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.TermsAndConditionsForCampaigns'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    // ftModal phase 2 #7 (sibling): Photo Release — same mode=html WYSIWYG, reuse FtHtmlFieldComponent.
    protected editReleaseTerms(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'releaseTerms' }],
            fieldsOptions: { ...AVAILABLE_TAGS },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.PhotoReleaseForCampaigns'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    // ftModal phase 2 #8: Consent for story submission — mode=html WYSIWYG + tokens + a readonlytext
    // default-text panel (FtHtmlFieldComponent renders the top `.modal-panel` + the 7 buttons + Insert-Token).
    protected editSubmitConsent(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'submitConsent' }],
            fieldsOptions: { ...AVAILABLE_TAGS, tokens: ['property'], readonlytext: this.model().submitConsentDefaultText },
            description: 'This message is displayed to audiences who are providing content. You can add a custom message below.',
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.ConsentForStorySubmission'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    // ftModal phase 2 / WS3: Consent for phone number — mode=html WYSIWYG + tokens, the sibling of
    // editSubmitConsent (no readonlytext panel). Native FtHtmlFieldComponent. The v1 `trimclipboardformat`
    // is a textAngular clipboard-sanitize option — render-inert + gate-blind (same deferred bucket as
    // AVAILABLE_TAGS); omitted. SAVE = full-PUT save(); cancel reverts the languages snapshot.
    protected editPhoneConsent(): void {
        this.languagesSnapshot = snapshotClone(this.data().contest.languages.item);
        this.fieldEditorConfig.set({
            type: 'html',
            datasource: this.data().contest.languages.item,
            fields: [{ name: 'phoneConsent' }],
            fieldsOptions: { ...AVAILABLE_TAGS, tokens: ['property'] },
            title: this.translate('flipto.account.sections.platform.photoAdvocacy.ConsentForPhoneNumber'),
            onDone: () => this.save(),
            onCancel: () => this.rollbackLanguages(),
            onClose: () => {
                this.languagesSnapshot = null;
            },
        });
    }

    // ftModal phase 2 #10: the FIRST native code editor — contest CSS. datasource is the flat
    // `data.contest` (field contestCss), so the shell's own per-field snapshot reverts on cancel
    // (no onCancel needed, like the bool fields). FtCodeFieldComponent mounts CodeMirror 6.
    protected editCss(): void {
        this.fieldEditorConfig.set({
            type: 'css',
            datasource: this.data().contest,
            fields: [{ name: 'contestCss' }],
            size: 'lg',
            title: this.translate('flipto.account.CSS'),
            onDone: () => this.save(),
        });
    }

    // --- the 4 named <ft-modal>s (built once into <body>, shown by id on click) ---

    protected openThemeModal(): void {
        if (this.themeLocked()) return;
        this.themeModalOpen.set(true);
    }

    /** Theme card click — v1 `ng-click="updateTheme(id); done()"`: direct-save the theme, then close. */
    protected selectTheme(theme: any): void {
        this.updateTheme(theme.photo_contest_theme_id);
        this.themeModalOpen.set(false);
    }
    // ftModal phase 2 / WS3: open the NATIVE date-range modal (replaces ftModal.show). Snapshot the
    // dates as dayjs clones (snapshotClone/structuredClone can't round-trip one) for X/Cancel.
    protected openDateRangeModal(): void {
        const c = this.data()?.contest;
        this.dateSnapshot = c && c.startDate && c.endDate ? { start: c.startDate.clone(), end: c.endDate.clone() } : null;
        this.dateRangeModalOpen.set(true);
    }

    /** Date-range modal Done (v1 on-done="saveContestDates"): persist, then close. */
    protected onDateRangeDone(): void {
        this.saveContestDates();
        this.dateRangeModalOpen.set(false);
    }

    /** Date-range modal X/Cancel (v1 ft-snapshot rollback): revert the dates to the open-time clones. */
    protected onDateRangeCancel(): void {
        if (this.dateSnapshot && this.data()?.contest) {
            this.data().contest.startDate = this.dateSnapshot.start;
            this.data().contest.endDate = this.dateSnapshot.end;
        }
        this.dateRangeModalOpen.set(false);
        this.dateSnapshot = null;
    }
    // ftModal phase 2 / WS3: open the native "Finalist ranking display" modal (replaces ftModal.show).
    // Snapshot isExactPlaceRanking (the value the dropdown mutates) for X/Cancel.
    protected openRankModal(): void {
        this.rankSnapshot = this.data().contest?.isExactPlaceRanking;
        this.rankOpen.set(false);
        this.rankModalOpen.set(true);
    }

    /** The 2-option ranking dropdown (v1 ng-click="data.contest.isExactPlaceRanking = true/false"). */
    protected setRank(exact: boolean): void {
        this.data().contest.isExactPlaceRanking = exact;
        this.rankOpen.set(false);
    }

    /** Rank modal Done (v1 on-done="save"): persist the full union, then close. */
    protected onRankDone(): void {
        this.save();
        this.closeRankModal();
    }

    /** Rank modal X/Cancel: revert isExactPlaceRanking to the open-time snapshot. */
    protected onRankCancel(): void {
        if (this.data().contest) this.data().contest.isExactPlaceRanking = this.rankSnapshot;
        this.closeRankModal();
    }

    private closeRankModal(): void {
        this.rankModalOpen.set(false);
        this.rankOpen.set(false);
        this.rankSnapshot = undefined;
    }
    // ftModal phase 2 / WS3: open the native "Banner image" modal (replaces ftModal.show). Snapshot
    // the v1 ft-snapshot targets (data.contest.backgroundImage + model.isUseDefaultImage) for X/Cancel.
    protected openImageLibraryModal(): void {
        this.bannerSnapshot = { backgroundImage: this.data().contest?.backgroundImage, isUseDefaultImage: this.model().isUseDefaultImage };
        this.bannerModalOpen.set(true);
    }

    /** The inline "use default image" toggle (replaces the v1 ft-switch on-change). */
    protected onBannerSwitchToggle(ev: Event): void {
        this.model().isUseDefaultImage = (ev.target as HTMLInputElement).checked;
        this.onUseBackgroundImage();
    }

    /** Banner modal Done (v1 on-done="updateBackgroundImage"): persist, then close. */
    protected onBannerDone(): void {
        this.updateBackgroundImage();
        this.closeBannerModal();
    }

    /** Banner modal X/Cancel (v1 ftModal snapshot.rollback): revert backgroundImage + isUseDefaultImage. */
    protected onBannerCancel(): void {
        if (this.bannerSnapshot) {
            if (this.bannerSnapshot.backgroundImage === undefined) delete this.data().contest.backgroundImage;
            else this.data().contest.backgroundImage = this.bannerSnapshot.backgroundImage;
            this.model().isUseDefaultImage = this.bannerSnapshot.isUseDefaultImage;
        }
        this.closeBannerModal();
    }

    private closeBannerModal(): void {
        this.bannerModalOpen.set(false);
        this.bannerSnapshot = null;
    }

    // --- save handlers (byte-identical to the v1 DetailsController) ---

    protected save(): void {
        const model = {
            languages: (this.data().contest.languages.item || []).map((language: any) => ({
                languageCode: language.code,
                followerMessage: language.followerMessage,
                guestMessage: language.guestMessage,
                contestTerms: language.terms,
                prizesTerms: this.setTargetBlankToLinks(language.prizesTerms),
                releaseTerms: this.setTargetBlankToLinks(language.releaseTerms),
                submitConsent: this.setTargetBlankToLinks(language.submitConsent),
                phoneConsent: this.setTargetBlankToLinks(language.phoneConsent),
                firstPrize: language.firstPrize,
                secondPrize: language.secondPrize,
                inviteeGreeting: language.inviteeGreeting,
                inviteeQuoteRequestMessage: language.inviteeQuoteRequestMessage,
                inviteeQuotePlaceRequestMessage: language.inviteeQuotePlaceRequestMessage,
            })),
            isAutoInvite: this.data().contest.isAutoInvite,
            isPhoneConsentFeatureEnabled: this.parseIsPhoneConsentFeatureEnabled(this.data().contest.isPhoneConsentFeatureEnabled),
            phoneConsentAcceptanceModeId: this.data().contest.phoneConsentAcceptanceModeId,
            isPrivate: this.data().contest.isPrivate,
            isExactPlaceRanking: this.data().contest.isExactPlaceRanking,
            isInviteeRequestPlaceForPhotoEnabled: this.data().contest.isInviteeRequestPlaceForPhotoEnabled,
            isInviteeRequestPlaceForPhotoRequired: this.data().contest.isInviteeRequestPlaceForPhotoRequired,
            allowedPlacesType: this.data().contest.allowedPlacesType,
            isInviteeRequestQuotes: this.data().contest.isInviteeRequestQuotes,
            contestCss: this.data().contest.contestCss,
            inviteeBackgroundImageUrl: this.data().contest.inviteeBackgroundImageUrl,
            theme: this.data().contest.themeId,
            inviteePhoneDisplayModeId: this.data().contest.inviteePhoneDisplayModeId,
            friendPhoneDisplayModeId: this.data().contest.friendPhoneDisplayModeId,
        };
        this.update(model).then((args: any) => this.broadcastChanged(args));
    }

    // WS5 #10 inc 2u: the DEAD `saveName(diffs)` was deleted — referenced nowhere
    // (its bridged compile wiring died with the native port; saveContestName is the
    // live native path).

    saveContestDates(): void {
        const model = {
            startDate: this.data().contest.startDate.format('YYYY-MM-DD'),
            endDate: this.data().contest.endDate.format('YYYY-MM-DD'),
        };
        this.ftTasks.register('update-email-images');
        this.update(model)
            .then((args: any) => this.broadcastChanged(args))
            .finally(() => this.ftTasks.finish('update-email-images'));
    }

    saveGoals(goals: any, type: any): void {
        const serialized = this.updateGoalsSerializer.serialize({ type, goals }, this.data().originalGoals);
        if (serialized.deleteGoals.length === 0 && serialized.newGoals.length === 0 && serialized.updateGoals.length === 0) return;
        this.update({ friendsGoals: serialized });
    }

    saveFinalistIncentives(): void {
        const fi = this.data().contest.finalistsIncentives;
        const model = {
            finalistsIncentives: {
                firstWinnerIncentiveUUID: fi.firstWinnerIncentive ? fi.firstWinnerIncentive.incentiveUUID : null,
                secondWinnerIncentiveUUID: fi.secondWinnerIncentive ? fi.secondWinnerIncentive.incentiveUUID : null,
                thirdWinnerIncentiveUUID: fi.thirdWinnerIncentive ? fi.thirdWinnerIncentive.incentiveUUID : null,
                finalistIncentiveUUID: fi.finalistIncentive ? fi.finalistIncentive.incentiveUUID : null,
                totalRunnerUps: fi.runnerUpsIncentive ? fi.runnerUpsIncentive['@totalRunnerUps'] : null,
                runnerUpsIncentiveUUID: fi.runnerUpsIncentive ? fi.runnerUpsIncentive['incentiveUUID'] : null,
            },
        };
        this.ftTasks.register('update-email-images');
        this.update(model)
            .then((args: any) => this.broadcastChanged(args))
            .finally(() => this.ftTasks.finish('update-email-images'));
    }

    /** Live data.contest.finalistsIncentives (the finalists modal's edit root). */
    protected readonly fi = computed<any>(() => this.data()?.contest?.finalistsIncentives);

    /** v1 header: "{GoalsFor} {Finalist|lowercase}" — the already-injected ngx translate (inc 2u). */
    protected get finalistsModalHeader(): string {
        return `${this.translate('flipto.components.goals.GoalsFor')} ${(this.translate('flipto.account.sections.platform.photoAdvocacy.Finalist') || '').toLowerCase()}`;
    }

    // Native read-view (FtFinalistIncentivesComponent) click → open the NATIVE finalists modal.
    // Snapshot data.contest.finalistsIncentives for the X/Cancel revert (replaces v1 ft-snapshot).
    protected openFinalistsGoalsModal(): void {
        this.finalistsSnapshot = snapshotClone(this.fi());
        this.finalistsModalOpen.set(true);
    }

    /** Finalists modal Done (v1 on-done="saveFinalistIncentives"): persist, then close. */
    protected onFinalistsDone(): void {
        this.saveFinalistIncentives();
        this.closeFinalistsModal();
    }

    /** Finalists modal X/Cancel (v1 ftModal snapshot.rollback): revert finalistsIncentives. */
    protected onFinalistsCancel(): void {
        if (this.finalistsSnapshot) {
            revertDiffs(this.fi(), this.finalistsSnapshot, computeDiffs(this.finalistsSnapshot, this.fi()));
        }
        this.closeFinalistsModal();
    }

    private closeFinalistsModal(): void {
        this.finalistsModalOpen.set(false);
        this.finalistsSnapshot = null;
    }

    /** Footer "add" buttons — v1 `ng-click="fi.X = {incentiveUUID: filteredIncentivesByTag[0].id}"`. */
    protected addFinalistSlot(slot: string): void {
        this.fi()[slot] = { incentiveUUID: this.filteredIncentivesByTag()[0] && this.filteredIncentivesByTag()[0].id };
    }

    /** Runner-ups add — v1 `{'@totalRunnerUps':1, incentiveUUID: filteredIncentivesByTag[0].id}`. */
    protected addRunnerUps(): void {
        this.fi().runnerUpsIncentive = { '@totalRunnerUps': 1, incentiveUUID: this.filteredIncentivesByTag()[0] && this.filteredIncentivesByTag()[0].id };
    }

    /** Delete-item buttons — v1 `ng-click="fi.X = null"`. */
    protected removeFinalistSlot(slot: string): void {
        this.fi()[slot] = null;
    }

    /**
     * Native read-view (FtGoalsPanelComponent) click → v1 ftGoalsPanel `showModal`:
     * build editGoalsModel from the friends (type=2) goals, then open the NATIVE
     * friends-goals modal. `updateGoals = $filter('filter')(goals, {type: 2})` — the
     * AngularJS substring match; goal types are single-digit so it equals type==2.
     */
    protected openGoalsModal(): void {
        const goals = (this.data()?.contest?.goals?.item as any[]) || [];
        this.editGoalsModel.set({
            newGoals: [],
            updateGoals: goals.filter((g: any) => ('' + g.type).indexOf('2') !== -1),
            deleteGoals: [],
        });
        this.goalsModalOpen.set(true);
    }

    /** Friends-goals modal Done (v1 ftGoalsPanel on-done="save"): persist, then close. */
    protected onGoalsDone(): void {
        this.saveGoalsFromModal();
        this.goalsModalOpen.set(false);
    }

    /** Friends-goals modal X/Cancel — v1 had NO ft-snapshot, so just close (no revert). */
    protected onGoalsCancel(): void {
        this.goalsModalOpen.set(false);
    }

    /**
     * The modal's Done path — v1 ftGoalsPanel `save`: split the goals flagged for
     * removal out of updateGoals into deleteGoals (their ids), then delegate to the
     * native saveGoals (which serialises + PUTs).
     */
    saveGoalsFromModal(): void {
        const m = this.editGoalsModel();
        if (!m) return;
        const removed = (m.updateGoals || []).filter((g: any) => g.markedForRemoval === true);
        m.updateGoals = (m.updateGoals || []).filter((g: any) => g.markedForRemoval !== true);
        m.deleteGoals = removed.map((g: any) => g.id);
        this.saveGoals(m, 2);
    }

    updateTheme(themeId: any): void {
        this.update({ theme: themeId }).then((args: any) => this.broadcastChanged(args));
    }

    protected updateImage(image: any): void {
        this.data().contest.backgroundImage = image ? image.url : null;
    }

    onUseBackgroundImage(): void {
        if (this.model().isUseDefaultImage) {
            delete this.data().contest.backgroundImage;
        }
    }

    updateBackgroundImage(): void {
        this.ftTasks.register('update-email-images');
        Promise.resolve(this.photoContest.updateBackgroundImage(this.spaces.getPropertyUuid(), this.contestUuid(), this.data().contest.backgroundImage))
            .then(() => this.loadContest(), () => this.loadContest())
            .then((args: any) => this.broadcastChanged(args))
            .finally(() => this.ftTasks.finish('update-email-images'));
    }

    /**
     * v1 `refreshPrearrivalImage` (details.controller.js:284-294) — regenerate one language's
     * pre-arrival banner, then finish the `generate-email-image-{imageUUID}-{code}` task the
     * gallery's Refresh button registered. (Restored with the previews gallery: it was deleted as
     * template-unreferenced while the gallery modal was missing from the read-view-only port.)
     */
    refreshPrearrivalImage(language: any): void {
        const imageUUID = language.imageUUID;
        Promise.resolve(this.photoContest.generatePreArrivalEmailImage(this.spaces.getPropertyUuid(), this.contestUuid(), language.code))
            .then((newImageUUID: any) => {
                language.imageUUID = newImageUUID;
                language.isImageProcessed = true;
            })
            .finally(() => this.ftTasks.finish('generate-email-image-' + imageUUID + '-' + language.code));
    }

    /** v1 `refreshPoststayImage` (details.controller.js:296-306). */
    refreshPoststayImage(language: any): void {
        const imageUUID = language.imageUUID;
        Promise.resolve(this.photoContest.generatePoststayEmailImage(this.spaces.getPropertyUuid(), this.contestUuid(), language.code))
            .then((newImageUUID: any) => {
                language.imageUUID = newImageUUID;
                language.isImageProcessed = true;
            })
            .finally(() => this.ftTasks.finish('generate-email-image-' + imageUUID + '-' + language.code));
    }

    /** v1 ft-disable-while-task-pending="update-email-images" on the two banner-preview panels. */
    isEmailImagesUpdating(): boolean {
        return this.ftTasks.isPending('update-email-images');
    }

    // WS5 #10 inc 2u: the DEAD `updateBannerImageStatus` was deleted — referenced nowhere
    // (its SignalR wiring never made the native port; contest details compiled the widget
    // with connect-to-hub="false").

    private broadcastChanged(args: any): void {
        // WS5 #10 inc 2i: native↔native (listener = the contest-container header) → the bus.
        this.eventBus.emit('flipto.photo-contest-changed', args && args[0] ? args[0] : this.data().contest);
    }

    private update(model: any): Promise<any> {
        return Promise.resolve(this.photoContest.update(this.spaces.getPropertyUuid(), this.contestUuid(), model))
            .then(() => this.loadContest(), () => this.loadContest());
    }

    private setTargetBlankToLinks(html: string): string {
        if (!html) return html;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('body')!;
        body.querySelectorAll('a').forEach((link) => {
            if (!link.hasAttribute('target') || link.getAttribute('target') !== '_blank') {
                link.setAttribute('target', '_blank');
            }
        });
        return body.innerHTML.replace(/&nbsp;/g, ' ');
    }

    private loadContest(): Promise<any> {
        const propertyUuid = this.spaces.getPropertyUuid();
        return Promise.all([
            Promise.resolve(this.photoContest.load({ contestId: this.contestUuid(), propertyUuid })),
            Promise.resolve(this.incentives.getAvailableIncentives(propertyUuid)),
            Promise.resolve(this.imageApi.getPropertyImages({ propertyId: propertyUuid })),
        ]).then((args: any[]) => {
            const contestDetails = args[0];
            const propertyIncentives = args[1].data;
            const propertyImages = args[2]['asset_images'] ? args[2]['asset_images'] : [];

            this.zone.run(() => {
                const contest = JSON.parse(JSON.stringify(contestDetails));
                this.data.set({
                    contest,
                    originalGoals: JSON.parse(JSON.stringify(contestDetails.goals ? contestDetails.goals.item : [])),
                    propertyIncentives,
                    propertyImages,
                });

                contest.subdomains = this.parseSvc.arrayOrDefault(contest.subdomains);
                contest.prearrivalEmailImageIsActive = this.parseSvc.bool(contest.prearrivalEmailImageIsActive);
                contest.poststayEmailImageIsActive = this.parseSvc.bool(contest.poststayEmailImageIsActive);
                contest.isAutoInvite = this.parseSvc.bool(contest.isAutoInvite);
                contest.isPrivate = this.parseSvc.bool(contest.isPrivate);
                contest.isExactPlaceRanking = this.parseSvc.bool(contest.isExactPlaceRanking);
                contest.isInviteeRequestPlaceForPhotoEnabled = this.parseSvc.bool(contest.isInviteeRequestPlaceForPhotoEnabled);
                contest.isInviteeRequestPlaceForPhotoRequired = this.parseSvc.bool(contest.isInviteeRequestPlaceForPhotoRequired);
                contest.allowedPlacesType = this.parseSvc.int(contest.allowedPlacesType);
                contest.isInviteeRequestQuotes = this.parseSvc.bool(contest.isInviteeRequestQuotes);
                contest.startDate = dayjs(contest.startDate);
                contest.endDate = dayjs(contest.endDate);

                this.model().isActive = this.parseSvc.bool(contest.isActive);
                this.model().language = find(contest.languages.item, { code: 'en' });
                this.model().isUseDefaultImage = !contestDetails.backgroundImage;

                // The filteredIncentivesByTag $watch (contestPrize-tagged incentives).
                const filteredIncentivesByTag: any[] = [];
                (propertyIncentives.item || []).forEach((incentive: any) => {
                    if ((incentive.categoryFlags & this.incentiveTags.contestPrize) === this.incentiveTags.contestPrize) {
                        filteredIncentivesByTag.push(incentive);
                    }
                });
                this.filteredIncentivesByTag.set(filteredIncentivesByTag);

                // The v1 ftGoalsPanel `filteredByTagIncentives` $watch (type=2 ⇒ warmLead-tagged
                // incentives), bound by the bridged friends-goals modal's <ft-incentive-selector>.
                const goalsIncentivesByTag: any = { item: [] };
                (propertyIncentives.item || []).forEach((incentive: any) => {
                    if ((incentive.categoryFlags & this.incentiveTags.warmLead) === this.incentiveTags.warmLead) {
                        goalsIncentivesByTag.item.push(incentive);
                    }
                });
                this.goalsIncentivesByTag.set(goalsIncentivesByTag);
            });

            return args;
        });
    }

    // ⭐ contest-details is now FULLY NATIVE — every previously-bridged <ft-modal> compile is gone
    // (date-range / image-library / theme / rank-system / finalists-goals / contest-goals →
    // native ft-modal-overlay components; email-image previews → ft-email-images-preview-ng; the
    // field editors use the native FtFieldEditorModalComponent). The FieldsEditorBridgeService
    // injection + the buildBridges()/maybeBuildBridges() machinery (+ AfterViewInit) are REMOVED.
}
